import { useEffect, useState } from 'react';
import { Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * DigestPreferenceCard — Profile toggle for the Sunday email digest.
 */
export default function DigestPreferenceCard() {
  const [optout, setOptout] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    api.get('/digest/preferences')
      .then((r) => setOptout(!!r.data?.digest_optout))
      .catch(() => setOptout(false))
      .finally(() => setLoading(false));
    // Auto-optout via query string (from email footer link)
    if (window.location.search.includes('digest=off')) {
      setOptout(true);
      api.post('/digest/optout', { optout: true })
        .then(() => toast.success("You're unsubscribed from the weekly digest."))
        .catch(() => {});
    }
  }, []);

  const toggle = async () => {
    setSaving(true);
    const next = !optout;
    try {
      await api.post('/digest/optout', { optout: next });
      setOptout(next);
      toast.success(next ? 'Unsubscribed from digest' : 'Subscribed to Sunday digest');
    } catch (e) {
      toast.error('Could not save preference.');
    } finally {
      setSaving(false);
    }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/digest/preview`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('preview failed');
      const html = await res.text();
      const win = window.open('', '_blank');
      if (win) { win.document.write(html); win.document.close(); }
    } catch {
      toast.error('Could not open preview.');
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) return null;

  return (
    <section data-testid="digest-preference-card">
      <h3 className="text-[10px] uppercase tracking-[0.25em] text-accent mb-3">Weekly Digest</h3>
      <div className="rounded-3xl bg-gradient-to-br from-blue-900/20 to-black/50 border border-blue-500/20 p-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-500/15 border border-blue-400/25 flex items-center justify-center">
            <Mail className="w-5 h-5 text-blue-300" strokeWidth={1.6} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-serif text-base">Sunday recap email</p>
            <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
              Top species observed this week, org announcements, and live raid alerts —
              sent every Sunday to keep you in the loop even when you're offline.
            </p>
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            onClick={toggle}
            disabled={saving}
            data-testid="digest-toggle-btn"
            className={`flex-1 rounded-xl text-xs font-semibold py-2.5 flex items-center justify-center gap-2 transition ${
              optout
                ? 'bg-blue-500 hover:bg-blue-400 text-black'
                : 'bg-white/5 hover:bg-white/10 border border-white/10 text-white'
            }`}
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {optout ? 'Re-subscribe' : 'Unsubscribe'}
          </button>
          <button
            onClick={preview}
            disabled={previewing}
            data-testid="digest-preview-btn"
            className="rounded-xl bg-black/40 hover:bg-black/60 border border-white/10 text-xs py-2.5 px-3"
            title="Open a live preview"
          >
            {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Preview'}
          </button>
        </div>
        {optout && (
          <p className="mt-2 text-[10px] text-muted-foreground text-center" data-testid="digest-optout-notice">
            You're currently opted out. Tap re-subscribe to opt back in.
          </p>
        )}
      </div>
    </section>
  );
}
